import React from 'react';
import { AlertCircle, ImageOff, ExternalLink } from 'lucide-react';

/**
 * Tabela comparativa Original x Similares propostos.
 *
 * Extraída do modal de Cancelamentos (SimilarProporModal) para o Atendimento
 * poder mostrar a MESMA comparação (pedido da Adnéia, 28/09): no card do
 * atendimento a comparação era uma lista compacta com miniaturas, sem EAN,
 * custo, subcategoria nem estoque por filial — dados que decidem se o similar
 * serve. Aqui é só APRESENTAÇÃO: quem busca os dados e o que fazer com a
 * seleção continua sendo de cada modal (o do atendimento gera os textos; o de
 * cancelamentos grava a proposta).
 *
 * imgMap[sku]: url  -> mostra a imagem
 *              ''   -> sem imagem (ícone)
 *              undefined -> ainda carregando
 * linkMap[sku]: url do cadastro do SKU no admin da VTEX (vem junto com a imagem,
 *               em /produtos/imagem). Sem ele o SKU aparece como texto.
 * lojaMap[sku]: url da página do produto na loja VTEX (idem). O clique na foto
 *               abre a loja, que tem todos os dados (Adnéia, 13/10/2026); sem
 *               ela, abre a imagem como antes. A loja pede login da VTEX: serve
 *               à equipe. O texto ao cliente continua com o link da imagem.
 */

// Para onde vai o clique na foto: a página da loja; sem ela, a própria imagem.
export function FotoLink({ img, loja, alt, className }) {
  return (
    <a href={loja || img} target="_blank" rel="noopener noreferrer"
      title={loja ? 'Abrir o produto na loja (VTEX)' : 'Abrir imagem'}>
      <img src={img} alt={alt} className={className} />
    </a>
  );
}

// SKU clicável que abre o cadastro na VTEX em outra aba (Adnéia, 07/10/2026).
// Usado também no Propor Similar do AES/ETR e no Buscar Produto. Dentro do
// <label> do checkbox o clique no link não marca o proposto.
export function SkuVtex({ sku, url }) {
  if (!url) return <>{sku}</>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
      title="Abrir o cadastro na VTEX"
      className="inline-flex items-center gap-0.5 text-blue-700 hover:underline dark:text-blue-300">
      {sku}<ExternalLink className="h-3 w-3" />
    </a>
  );
}

const formatMoney = (v) => {
  const n = Number(v);
  return isFinite(n) && n !== 0
    ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';
};

const somaVals = (obj) => Object.values(obj || {}).reduce((a, b) => a + Number(b || 0), 0);

export default function ComparativoSimilares({ original, propostos = [], selecionados, onToggle, imgMap = {}, linkMap = {}, lojaMap = {} }) {
  if (!original) return null;

  // Δ de custo do proposto vs. original (vermelho = mais caro, verde = mais barato)
  const custoDelta = (p) => {
    const co = Number(original?.custo), cp = Number(p?.custo);
    if (!isFinite(co) || !isFinite(cp) || co === 0) return null;
    const dif = cp - co;
    if (!dif) return null;
    return <span className={`text-[11px] ml-1 ${dif > 0 ? 'text-red-600' : 'text-emerald-600'}`}>({dif > 0 ? '+' : ''}{formatMoney(dif)})</span>;
  };

  const imgCell = (p, idx) => {
    const url = imgMap[p.cod_terceiro];
    return (
      <td key={`img-${p.cod_terceiro}-${idx}`} className={`px-3 py-2 ${idx === 0 ? 'bg-amber-50/30 dark:bg-amber-950/10' : ''}`}>
        {url
          ? <FotoLink img={url} loja={lojaMap[p.cod_terceiro]} alt={p.cod_terceiro} className="h-24 w-24 object-contain rounded border bg-white" />
          : url === ''
            ? <span className="inline-flex h-24 w-24 items-center justify-center rounded border bg-slate-50 text-slate-300"><ImageOff className="h-6 w-6" /></span>
            : <span className="inline-flex h-24 w-24 items-center justify-center rounded border bg-slate-50 text-[10px] text-slate-400">carregando…</span>}
      </td>
    );
  };

  const linhas = [
    { label: 'cod_terceiro', get: p => <SkuVtex sku={p.cod_terceiro} url={linkMap[p.cod_terceiro]} /> },
    { label: 'ID Bseller', get: p => p.id_item_bseller || '—' },
    { label: 'Cód. fornecedor', get: p => p.codigo_fornec || '—' },
    { label: 'EAN', get: p => p.ean || '—' },
    { label: 'Descrição', get: p => p.descricao || '—' },
    { label: 'Status', get: p => `${p.status || '—'}${p.abc_class ? ` · classe ${p.abc_class}` : ''}` },
    // Subcategoria na tela: com o escopo amplo o proposto pode vir de outra
    // (Eletroportateis x Cozinha e Utilidades = eletrico x manual).
    { label: 'Subcategoria', get: p => p.subcategoria || p.categoria || '—' },
    { label: 'Peças', get: p => (p.pecas != null ? `${p.pecas} peças` : '—') },
    { label: 'Preço de venda', get: p => <span className="font-semibold text-emerald-700">{formatMoney(p.preco_venda)}</span> },
    { label: 'Custo (últ. compra)', get: p => <span>{formatMoney(p.custo)}{p !== original && custoDelta(p)}</span> },
    { label: 'Físico (total)', get: p => somaVals(p.filiais_fisico) || 0 },
    { label: 'XD (total)', get: p => somaVals(p.xd_por_estab) || 0 },
    { label: 'UFs com estoque', get: p => (p.ufs_com_estoque || []).join(', ') || '—' },
  ];

  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="border-b">
          <th className="px-3 py-2 text-left bg-slate-50 dark:bg-slate-800 w-44">&nbsp;</th>
          <th className="px-3 py-2 text-left align-top bg-amber-50/40 dark:bg-amber-950/20">
            <div className="font-semibold text-sm"><SkuVtex sku={original.cod_terceiro} url={linkMap[original.cod_terceiro]} /> — original</div>
          </th>
          {propostos.map((p, i) => (
            <th key={p.cod_terceiro} className="px-3 py-2 text-left align-top">
              <label className="flex items-start gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selecionados?.has(p.cod_terceiro) || false}
                  onChange={() => onToggle?.(p.cod_terceiro)}
                  className="mt-0.5 accent-green-600"
                />
                <div>
                  <div className="font-semibold text-sm"><SkuVtex sku={p.cod_terceiro} url={linkMap[p.cod_terceiro]} /> — Proposto ({i + 1})</div>
                  {(p.venda_pct != null || p.acima_do_pago || p.custo_situacao === 'acima_do_teto' || p.pecas != null) && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {p.pecas != null && (
                        <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                          {p.pecas} peças
                        </span>
                      )}
                      {p.venda_pct != null && (
                        <span
                          className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            p.venda_situacao === 'muito_abaixo' ? 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300'
                            : p.venda_situacao === 'muito_acima' ? 'bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300'
                            : p.venda_situacao === 'ate_20_abaixo' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                            : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'}`}
                          title="Preço de venda do proposto em relação ao original"
                        >
                          {p.venda_pct}% do valor
                        </span>
                      )}
                      {p.acima_do_pago && (
                        <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300"
                          title="Custa mais do que o cliente pagou — a troca dá prejuízo">
                          custa + que o pago
                        </span>
                      )}
                      {p.custo_situacao === 'acima_do_teto' && !p.acima_do_pago && (
                        <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/20 dark:text-amber-300 dark:border-amber-800"
                          title={p.custo_dif != null ? `Custo R$ ${p.custo_dif.toFixed(2).replace('.', ',')} acima do original (teto era R$ 30)` : 'Custo acima do teto de R$ 30'}>
                          custo +R$ {p.custo_dif != null ? p.custo_dif.toFixed(2).replace('.', ',') : '?'}
                        </span>
                      )}
                      {p.custo_situacao === 'sem_custo' && (
                        <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                          title="Custo não cadastrado — não dá pra avaliar margem">
                          sem custo
                        </span>
                      )}
                    </div>
                  )}
                  {p.escopo_amplo && (
                    <div className="mt-0.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100"
                      title="Veio de outra subcategoria: confira se o tipo bate (elétrico x manual) antes de ofertar">
                      fora da subcategoria
                    </div>
                  )}
                  {p.alerta_filial && (
                    <div className="mt-1 text-xs flex items-start gap-1 text-red-600 font-medium">
                      <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /><span>{p.alerta_filial}</span>
                    </div>
                  )}
                </div>
              </label>
            </th>
          ))}
          {propostos.length === 0 && (
            <th className="px-3 py-4 text-center text-slate-500 font-normal">Nenhum similar com estoque encontrado.</th>
          )}
        </tr>
      </thead>
      <tbody>
        <tr className="border-b">
          <th className="px-3 py-1.5 text-left font-medium text-slate-600 bg-slate-50 dark:bg-slate-800">Imagem</th>
          {[original, ...propostos].map((p, idx) => imgCell(p, idx))}
        </tr>
        {linhas.map(({ label, get }) => (
          <tr key={label} className="border-b">
            <th className="px-3 py-1.5 text-left font-medium text-slate-600 bg-slate-50 dark:bg-slate-800">{label}</th>
            <td className="px-3 py-1.5 bg-amber-50/30 dark:bg-amber-950/10">{get(original) ?? '—'}</td>
            {propostos.map(p => <td key={p.cod_terceiro + label} className="px-3 py-1.5">{get(p) ?? '—'}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
